import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useBrowserCapabilities } from './useBrowserCapabilities.js';
import { selectOptimalSource } from './useOptimalSource.js';
import {
  VideoPlayer,
  VideoPlayerContent,
  VideoPlayerControlBar,
  VideoPlayerPlayButton,
  VideoPlayerTimeRange,
  VideoPlayerTimeDisplay,
  VideoPlayerMuteButton,
  VideoPlayerVolumeRange,
  VideoPlayerFullscreenButton,
  VideoPlayerBigPlayButton,
  VideoPlayerSurfaceToggle,
} from './controls/index.js';
import type { PreferredCodec, PreferredSize, SelectedSource, VideoData } from '../types.js';
import { fetchVideoData } from '../utils/api.js';

type NativeVideoProps = Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'src' | 'poster'> & {
  poster?: string | false;
  // Allow direct src fallback when mediaId is not provided
  src?: string;
};

export type VideoProps = NativeVideoProps & {
  /** DashTrack media record ID. If omitted, provide `src` for direct progressive playback. */
  mediaId?: number;
  /** Optional custom CDN host, defaults to https://edge.dashtrack.com */
  cdnHost?: string;
  /** Force adaptive streaming if available. */
  adaptiveStreaming?: boolean;
  /** Upgrade to streaming on interaction (e.g., play). */
  streamingOnInteraction?: boolean;
  /** Disable streaming upgrades, stick to progressive playback. */
  streamingUpgrade?: boolean;
  /** Optional preferred codec (HLS, DASH, WEBM, MP4). */
  preferCodec?: PreferredCodec;
  /** Preferred size variant when using progressive codecs. */
  preferSize?: PreferredSize;
  /** Callback when video data is loaded. */
  onVideoData?: (data: VideoData) => void;
};

/**
 * Video renders a plain <video> element (no wrapper, no default styles)
 * so it behaves exactly like a native video tag for styling/layout.
 */
const VideoBase = forwardRef<HTMLVideoElement, VideoProps>(function Video(
  {
    mediaId,
    cdnHost,
    adaptiveStreaming = false,
    streamingOnInteraction = false,
    streamingUpgrade = true,
    preferCodec,
    preferSize,
    poster,
    onVideoData,
    ...rest
  },
  ref
) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement);

  const capabilities = useBrowserCapabilities();

  const [videoData, setVideoData] = useState<VideoData | null>(null);
  const [selected, setSelected] = useState<SelectedSource | null>(null);
  const prevSelectedRef = useRef<SelectedSource | null>(null);

  const directSrc = (rest as any)?.src as string | undefined;
  const hasMediaId = Number.isFinite(mediaId as number);

  // Fetch video data from CDN
  useEffect(() => {
    if (!hasMediaId) {
      setVideoData(null);
      setSelected(null);
      return;
    }
    const controller = new AbortController();
    setVideoData(null);
    setSelected(null);
    fetchVideoData(mediaId as number, { cdnHost, signal: controller.signal })
      .then((data) => {
        setVideoData(data);
        onVideoData?.(data);
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('Video data fetch failed:', err);
        }
      });
    return () => controller.abort();
  }, [hasMediaId, mediaId, cdnHost, onVideoData]);

  // Choose initial source
  useEffect(() => {
    if (!videoData) return;
    const choice = selectOptimalSource(videoData, capabilities, preferCodec, preferSize);
    const prev = prevSelectedRef.current;
    if (!choice) {
      if (prev !== null) {
        prevSelectedRef.current = null;
        setSelected(null);
      }
      return;
    }
    if (!prev || prev.src !== choice.src || prev.method !== choice.method || prev.type !== choice.type) {
      prevSelectedRef.current = choice;
      setSelected(choice);
    }
  }, [videoData, capabilities, preferCodec, preferSize]);

  // Streaming adapters lifecycle
  const cleanupRef = useRef<() => void>();
  const attachHls = useCallback(async (src: string) => {
    const { attachHls } = await import('../streaming/hls-adapter.js');
    if (videoRef.current) {
      const ctl = await attachHls(videoRef.current, src);
      if (ctl) cleanupRef.current = () => ctl.detach();
    }
  }, []);
  const attachDash = useCallback(async (src: string) => {
    const { attachDash } = await import('../streaming/dash-adapter.js');
    if (videoRef.current) {
      const ctl = await attachDash(videoRef.current, src);
      if (ctl) cleanupRef.current = () => ctl.detach();
    }
  }, []);

  const activeModeRef = useRef<{ mode: 'progressive' | 'hls' | 'dash'; src: string } | null>(null);

  const detachAdapters = useCallback(() => {
    try {
      cleanupRef.current?.();
    } finally {
      cleanupRef.current = undefined;
      activeModeRef.current = null;
    }
  }, []);

  // Apply source / streaming
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Always cleanup previous adapter when changing source
    detachAdapters();

    const applyProgressive = () => {
      // Avoid resetting the same source repeatedly
      const targetSrc = (selected ? selected.src : directSrc) ?? '';
      const currentAttrSrc = video.getAttribute('src') || '';
      if (targetSrc && (currentAttrSrc !== targetSrc || activeModeRef.current?.mode !== 'progressive')) {
        video.setAttribute('src', targetSrc);
        activeModeRef.current = { mode: 'progressive', src: targetSrc };
      }
      // type is advisory; browsers infer from resource headers as well
    };

    const applyStreaming = async (force = false) => {
      if (!force && !streamingUpgrade) return applyProgressive();
      // Native HLS path (Safari)
      if (selected && selected.method === 'native_hls') {
        const currentAttrSrc = video.getAttribute('src') || '';
        if (currentAttrSrc !== selected.src || activeModeRef.current?.mode !== 'progressive') {
          video.setAttribute('src', selected.src);
          activeModeRef.current = { mode: 'progressive', src: selected.src };
        }
        return;
      }
      // Non-native: choose library
      if (selected && selected.method === 'hls') {
        if (activeModeRef.current?.mode !== 'hls' || activeModeRef.current?.src !== selected.src) {
          await attachHls(selected.src);
          activeModeRef.current = { mode: 'hls', src: selected.src };
        }
        return;
      }
      if (selected && selected.method === 'dash') {
        if (activeModeRef.current?.mode !== 'dash' || activeModeRef.current?.src !== selected.src) {
          await attachDash(selected.src);
          activeModeRef.current = { mode: 'dash', src: selected.src };
        }
        return;
      }
      // Handle fallback URLs as progressive
      if (selected && selected.method === 'fallback') {
        applyProgressive();
        return;
      }
      // Otherwise, progressive and maybe upgradable
      applyProgressive();
    };

    // If direct src provided (no mediaId), keep progressive behavior only.
    if (directSrc && !hasMediaId) {
      applyProgressive();
      return () => {
        detachAdapters();
      };
    }

    const streamingRequired = selected?.method === 'hls' || selected?.method === 'dash';

    if (selected && (adaptiveStreaming || streamingRequired)) {
      void applyStreaming(streamingRequired);
    } else {
      applyProgressive();
    }

    return () => {
      detachAdapters();
    };
  }, [selected, adaptiveStreaming, streamingUpgrade, attachHls, attachDash, detachAdapters, directSrc, hasMediaId]);

  // Mobile-specific autoplay handling
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    // Handle mobile Safari autoplay restrictions
    const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    
    if (isMobileSafari && video.autoplay) {
      // For mobile Safari, attempt autoplay with muted attribute
      video.muted = true;
      
      // Use a timeout to ensure the video element is fully loaded
      const autoplayTimer = setTimeout(() => {
        if (video.readyState >= 2) { // HAVE_CURRENT_DATA
          video.play().catch(err => {
            // eslint-disable-next-line no-console
            console.warn('Mobile autoplay failed:', err);
          });
        }
      }, 100);
      
      return () => clearTimeout(autoplayTimer);
    }
  }, []);

  // Streaming upgrade on interaction (e.g., play)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !streamingOnInteraction || !videoData || !selected) return;
    const onPlay = async () => {
      try {
        // Upgrade only if not already streaming
        if (selected.method === 'progressive_webm' || selected.method === 'progressive_mp4') {
          // Prefer HLS when available (CDN URL if present, else chosen full_size)
          const hlsUrl =
            videoData.variants_data.variants.HLS?.cdn_master_playlist_url ||
            videoData.variants_data.variants.HLS?.full_size;
          const dashUrl =
            videoData.variants_data.variants.DASH?.cdn_manifest_url ||
            videoData.variants_data.variants.DASH?.full_size;
          if (hlsUrl) {
            if (activeModeRef.current?.mode !== 'hls' || activeModeRef.current?.src !== hlsUrl) {
              await detachAdapters();
              await attachHls(hlsUrl);
              activeModeRef.current = { mode: 'hls', src: hlsUrl };
            }
            return;
          }
          if (dashUrl) {
            if (activeModeRef.current?.mode !== 'dash' || activeModeRef.current?.src !== dashUrl) {
              await detachAdapters();
              await attachDash(dashUrl);
              activeModeRef.current = { mode: 'dash', src: dashUrl };
            }
            return;
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Streaming upgrade failed, continuing progressive:', e);
      }
    };
    video.addEventListener('play', onPlay, { once: true });
    return () => video.removeEventListener('play', onPlay);
  }, [streamingOnInteraction, videoData, selected, attachDash, attachHls, detachAdapters]);

  // Pass-through poster handling: allow string or false to skip entirely
  const posterAttr = useMemo(() => {
    if (poster === false) return undefined;
    if (typeof poster === 'string') return poster;
    // Prefer CDN-provided poster when available
    const cdnPoster = (videoData?.poster_url as string | undefined) ?? undefined;
    if (cdnPoster) return cdnPoster;
    // Fallback: low-res thumb while metadata loads
    if (hasMediaId && typeof mediaId === 'number' && Number.isFinite(mediaId)) {
      const host = (cdnHost || 'https://edge.dashtrack.com').replace(/\/$/, '');
      return `${host}/assets/low_res_thumb/${mediaId}`;
    }
    return undefined;
  }, [poster, videoData, hasMediaId, mediaId, cdnHost]);

  // Enhanced poster handling with autoplay awareness
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !posterAttr) return;
    
    const isMobile = /iPad|iPhone|iPod|Android/.test(navigator.userAgent);
    const isAutoplayVideo = !!video.autoplay;
    
    // Always ensure poster attribute is set when available
    if (video.poster !== posterAttr) {
      video.poster = posterAttr;
    }
    
    // For non-autoplay videos on mobile, ensure poster shows with play button
    if (isMobile && !isAutoplayVideo) {
      // Preload poster image and ensure it displays correctly
      const img = new Image();
      img.onload = () => {
        if (video.poster !== posterAttr) {
          video.poster = posterAttr;
        }
        // Ensure the poster is visible by pausing if needed
        if (!video.paused) {
          video.pause();
        }
      };
      img.onerror = () => {
        // eslint-disable-next-line no-console
        console.warn('Failed to load video poster:', posterAttr);
      };
      img.src = posterAttr;
      
      // Add event listeners to maintain poster on pause
      const handleLoadedMetadata = () => {
        if (video.poster !== posterAttr) {
          video.poster = posterAttr;
        }
      };
      
      const handlePause = () => {
        if (video.poster !== posterAttr) {
          video.poster = posterAttr;
        }
      };
      
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('pause', handlePause);
      
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('pause', handlePause);
      };
    } else {
      // For autoplay videos or desktop, just set the poster simply
      const img = new Image();
      img.onload = () => {
        if (video.poster !== posterAttr) {
          video.poster = posterAttr;
        }
      };
      img.onerror = () => {
        // eslint-disable-next-line no-console
        console.warn('Failed to load video poster:', posterAttr);
      };
      img.src = posterAttr;
    }
  }, [posterAttr]);

  // Compute default accessibility/title attributes from content_manifest
  const ariaLabelAttr = useMemo(() => {
    const provided = (rest as any)['aria-label'] as string | undefined;
    if (provided) return provided;
    const summary = videoData?.meta && (videoData.meta as any).content_manifest?.summary;
    if (!summary) return undefined;
    const s = String(summary);
    return s.length > 120 ? `${s.slice(0, 117)}...` : s;
  }, [videoData, rest]);

  const titleAttr = useMemo(() => {
    const provided = (rest as any).title as string | undefined;
    if (provided) return provided;
    return (videoData?.meta && (videoData.meta as any).content_manifest?.title) || undefined;
  }, [videoData, rest]);

  // Default preload and intrinsic size to reduce CLS
  const preloadAttr = useMemo(() => {
    const provided = (rest as any).preload as string | undefined;
    return provided ?? 'metadata';
  }, [rest]);

  const widthAttr = useMemo(() => {
    const provided = (rest as any).width as number | string | undefined;
    if (provided != null) return provided as any;
    return videoData?.variants_data?.metadata?.width ?? undefined;
  }, [rest, videoData]);

  const heightAttr = useMemo(() => {
    const provided = (rest as any).height as number | string | undefined;
    if (provided != null) return provided as any;
    return videoData?.variants_data?.metadata?.height ?? undefined;
  }, [rest, videoData]);

  // Render a plain <video> element — no wrapper, no default styles
  const sanitizedProps = useMemo(() => {
    const r: any = { ...rest };
    // Map playsinline -> playsInline if present
    if (Object.prototype.hasOwnProperty.call(r, 'playsinline')) {
      r.playsInline = r.playsinline;
      delete r.playsinline;
    }
    if (typeof r.playsInline === 'undefined') r.playsInline = true;
    // Strip non-DOM props that page builders sometimes pass through
    delete r.respectContainerDimensions;
    delete r.editable;
    // Do not forward src directly; we manage it via effect to avoid churn
    delete r.src;
    // Never forward native controls; our UI manages controls
    delete r.controls;
    // Always hide native Download menu; our UI handles downloads separately (currently disabled)
    const cl = (r.controlsList as string | undefined)?.trim();
    if (!cl) r.controlsList = 'nodownload';
    else if (!cl.includes('nodownload')) r.controlsList = `${cl} nodownload`;
    return r;
  }, [rest]);
  const showControls = Boolean((rest as any).controls);
  const isAutoplay = Boolean((rest as any).autoplay);

  return (
    <VideoPlayer className={(rest as any).className} style={(rest as any).style}>
      <VideoPlayerContent
        ref={videoRef}
        poster={posterAttr}
        aria-label={ariaLabelAttr}
        title={titleAttr}
        preload={preloadAttr as any}
        width={widthAttr as any}
        height={heightAttr as any}
        // Native controls should always be hidden; our control bar is used instead
        controls={false}
        {...sanitizedProps}
      />
      {/* Full-surface overlay to toggle play/pause while preserving rounded corners - hide for autoplay videos */}
      {!isAutoplay && <VideoPlayerSurfaceToggle />}
      {/* Big centered play overlay when paused - hide for autoplay videos */}
      {!isAutoplay && <VideoPlayerBigPlayButton />}
      {/* Custom control bar — visibility governed by props.controls */}
      <VideoPlayerControlBar show={showControls}>
        <VideoPlayerPlayButton />
        {/* Seek buttons omitted by default for MVP; can be added later */}
        <VideoPlayerTimeRange />
        <VideoPlayerTimeDisplay showDuration />
        <VideoPlayerMuteButton />
        <VideoPlayerVolumeRange />
        <VideoPlayerFullscreenButton />
      </VideoPlayerControlBar>
    </VideoPlayer>
  );
});

export const Video = memo(VideoBase);
Video.displayName = 'OpenSiteVideo';
